import * as React from "react";
import { MenuItemLink, useSidebarState } from "react-admin";
import { useLocation } from "react-router-dom";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
} from "@mui/material";

//Icons
import {
  AdminPanelSettings,
  Archive,
  Work,
  MonetizationOn,
  Map,
  Restaurant,
  Receipt,
  Inventory,
  Group,
  Bookmarks,
  BookmarkAdded,
  ExtensionTwoTone,
  PublishedWithChanges,
  ExitToApp,
  Book,
  RoomService,
  FindReplace,
  Storefront,
  AssessmentRounded,
} from "@mui/icons-material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import MonitorWeightIcon from "@mui/icons-material/MonitorWeight";
import MemoryIcon from "@mui/icons-material/Memory";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import FeedIcon from "@mui/icons-material/Feed";
import StoreIcon from "@mui/icons-material/Store";
import CelebrationIcon from "@mui/icons-material/Celebration";
import HiveIcon from "@mui/icons-material/Hive";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import FastfoodRoundedIcon from "@mui/icons-material/FastfoodRounded";
import House from "@mui/icons-material/House";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import ApartmentIcon from "@mui/icons-material/Apartment";
import Place from "@mui/icons-material/Place";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import NavigationIcon from "@mui/icons-material/Navigation";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Money from "@mui/icons-material/Money";
import CurrencyExchange from "@mui/icons-material/CurrencyExchange";
import Discount from "@mui/icons-material/Discount";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import UploadIcon from "@mui/icons-material/Upload";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import AccessTime from "@mui/icons-material/AccessTime";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RuleIcon from "@mui/icons-material/Rule";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CampaignIcon from "@mui/icons-material/Campaign";
import WebIcon from "@mui/icons-material/Web";

//Utils
import { useAppSelector } from "../../store/hooks";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";

type MenuItemConfig = {
  label: string;
  to: string;
  icon: React.ReactElement;
  permission: string;
  requiredGroups: UserGroupsEnum[];
  children?: Omit<MenuItemConfig, 'children'>[];
};

type MenuGroupConfig = {
  id: string;
  label: string;
  icon: React.ReactElement;
  items: MenuItemConfig[];
};

const MENU_GROUPS: MenuGroupConfig[] = [

  
  {
    id: "operations",
    label: "Operations",
    icon: <Work />,
    items: [
      // 1
      {
        label: "Chefit",
        to: "/OneMealService",
        icon: <Restaurant />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      // 2
      {
        label: "Chef For Party",
        to: "/ChefForParty",
        icon: <CelebrationIcon />,
        permission: "ChefForParty",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
        children: [
          {
            label: "Book by Dish",
            // Dedicated resource so its filters persist independently of the
            // occasion list (react-admin keys the filter store by resource).
            to: "/BookByDish",
            icon: <DinnerDiningIcon />,
            permission: "ChefForParty",
            requiredGroups: [
              UserGroupsEnum.ADMIN,
              UserGroupsEnum.EXECUTIVE,
              UserGroupsEnum.MANAGER,
            ],
          },
          {
            label: "Book by Occasion",
            to: "/ChefForParty",
            icon: <CelebrationIcon />,
            permission: "ChefForParty",
            requiredGroups: [
              UserGroupsEnum.ADMIN,
              UserGroupsEnum.EXECUTIVE,
              UserGroupsEnum.MANAGER,
            ],
          },
        ],
      },
      {
        label: "Multi Day Chefit",
        to: "/MultiDayChefit",
        icon: <FastfoodRoundedIcon />,
        permission: "MultiDayChefit",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Sos Alerts",
        to: "/SosAlerts",
        icon: <AccountBalanceWalletIcon />,
        permission: "SosAlerts",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Vendor Payouts",
        to: "/VendorPayouts",
        icon: <MonetizationOn />,
        permission: "VendorPayouts",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 3
      {
        label: "Pack",
        to: "/Combo",
        icon: <HiveIcon />,
        permission: "Combo",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 4
      {
        label: "Pack Discount Calculator",
        to: "/ComboDiscountCalulator",
        icon: <CalculateRoundedIcon />,
        permission: "Combo",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 5
      {
        label: "Visit Tracker",
        to: "/VisitTracker",
        icon: <ContentPasteSearchIcon />,
        permission: "VisitTracker",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      // 6
      {
        label: "Cooks",
        to: "/Cooks",
        icon: <Restaurant />,
        permission: "EmployeeRoster",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 7
      {
        label: "Cooks Serve Calendar",
        to: "/CooksServeCalendar",
        icon: <PermContactCalendarIcon />,
        permission: "CooksServeCalendar",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Houses",
        to: "/Houses",
        icon: <House />,
        permission: "Houses",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 10

      // 12
      {
        label: "Customers",
        to: "/Customers",
        icon: <Group />,
        permission: "Customers",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 13

      // 16
      {
        label: "VAS",
        to: "/Vas",
        icon: <AutoAwesome />,
        permission: "Vas",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 17
      {
        label: "VAS Transactions",
        to: "/VasTransactions",
        icon: <Receipt />,
        permission: "VasTransactions",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 18
      {
        label: "Refund V2",
        to: "/RefundV2",
        icon: <Money />,
        permission: "Refund",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 19
      {
        label: "All Transactions",
        to: "/Transactions",
        icon: <CurrencyExchange />,
        permission: "Transactions",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 20
      {
        label: "Coupons V2",
        to: "/CouponsV2",
        icon: <Discount />,
        permission: "Coupons",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 21
      {
        label: "Coupon SKU",
        to: "/CouponSku",
        icon: <StoreIcon />,
        permission: "Coupons",
        requiredGroups: [UserGroupsEnum.ADMIN],
      },

      // 22
      {
        label: "Cook Leaves",
        to: "/CookLeaves",
        icon: <CalendarMonth />,
        permission: "CookLeaves",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 23
      {
        label: "Bulk Upload Leads",
        to: "/BulkUploadLeads",
        icon: <UploadIcon />,
        permission: "BulkUploadLeads",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.EXECUTIVE,
        ],
      },

      // 24
      {
        label: "Daily Cook Slots",
        to: "/DailyCookSlot",
        icon: <ViewTimelineIcon />,
        permission: "DailyCookSlot",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 25
      {
        label: "Cook Slots",
        to: "/CookSlots",
        icon: <AccessTime />,
        permission: "CookSlots",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 26
      {
        label: "Platform",
        to: "/Platform",
        icon: <CelebrationIcon />,
        permission: "Platform",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      {
        label: "Wallet Transactions",
        to: "/WalletTransactions",
        icon: <AccountBalanceWalletIcon />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      {
        label: "Vendor Tips",
        to: "/VendorTip",
        icon: <AttachMoneyIcon />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      {
        label: "Promotional Balance",
        to: "/PromotionalBalance",
        icon: <AttachMoneyIcon />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      {
        label: "Bulk Promotional Balance",
        to: "/BulkPromotionalBalance",
        icon: <AttachMoneyIcon />,
        permission: "BulkPromotionalBalance",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },

      // 27
      {
        label: "Price Calculator",
        to: "/PriceCalculator",
        icon: <PointOfSaleIcon />,
        permission: "PriceCalculator",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
    ],
  },

  {
    id: "administration",
    label: "Administration",
    icon: <AdminPanelSettings />,
    items: [
      {
        label: "App Constants",
        to: "/AppConstants",
        icon: <StoreIcon />,
        permission: "Coupons",
        requiredGroups: [UserGroupsEnum.ADMIN],
      },
      {
        label: "Hubs",
        to: "/Hubs",
        icon: <MemoryIcon />,
        permission: "Hubs",
        requiredGroups: [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER],
      },
      {
        label: "MG Allocation",
        to: "/MGAllocation",
        icon: <Map />,
        permission: "MgAllocation",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Cook Live Location",
        to: "/CookLiveLocation",
        icon: <NavigationIcon />,
        permission: "CookLiveLocation",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "House Location",
        to: "/HouseLocation",
        icon: <House />,
        permission: "HouseLocation",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Serviceability Map",
        to: "/ServiceabilityMap",
        icon: <Map />,
        permission: "ServiceabilityMap",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Carts",
        to: "/PendingCarts",
        icon: <ShoppingCartIcon />,
        permission: "PendingCarts",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      {
        label: "Cuisine Dish",
        to: "/CuisineDish",
        icon: <DinnerDiningIcon />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Dish Category Tag",
        to: "/DishCategoryTag",
        icon: <LocalOfferIcon />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Admin Users",
        to: "/AdminUsers",
        icon: <AdminPanelSettings />,
        permission: "AdminUsers",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Assessment Resources",
        to: "/AssessmentResources",
        icon: <AssessmentRounded />,
        permission: "EmployeeRoster",
        requiredGroups: [UserGroupsEnum.ADMIN, UserGroupsEnum.PROMOTOR],
      },
      {
        label: "Employee Roster",
        to: "/EmployeeRoster",
        icon: <SupervisedUserCircleIcon />,
        permission: "EmployeeRoster",
        requiredGroups: [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER],
      },
      {
        label: "Additional Field Filter",
        to: "/CreateField",
        icon: <FeedIcon />,
        permission: "CreateField",
        requiredGroups: [UserGroupsEnum.ADMIN],
      },
      {
        label: "House Banners",
        to: "/HouseBanners",
        icon: <PhotoLibraryRoundedIcon />,
        permission: "Houses",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 11
      {
        label: "House Reviews",
        to: "/HouseReviews",
        icon: <ApartmentIcon />,
        permission: "HouseReviews",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      {
        label: "Cities",
        to: "/Cities",
        icon: <Place />,
        permission: "Cities",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 14
      {
        label: "Areas",
        to: "/Areas",
        icon: <AddLocationAltIcon />,
        permission: "Areas",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },

      // 15
      {
        label: "Localities",
        to: "/Localities",
        icon: <NavigationIcon />,
        permission: "Localities",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Reason / Sub-reason",
        to: "/ReasonSubreason",
        icon: <RuleIcon />,
        permission: "ReasonSubreason",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
    ],
  },


  {
    id: "cms",
    label: "CMS",
    icon: <WebIcon />,
    items: [
      {
        label: "Campaign Pages",
        to: "/CampaignPages",
        icon: <CampaignIcon />,
        permission: "CampaignPages",
        requiredGroups: [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER],
      },
    ],
  },

  {
    id: "archive",
    label: "Archive",
    icon: <Archive />,
    items: [
      {
        label: "Vendors",
        to: "/Vendors",
        icon: <Storefront />,
        permission: "Vendors",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "User Vas Mapping",
        to: "/UserBundleServiceMappings",
        icon: <RoomService />,
        permission: "UserBundleServiceMappings",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Plans",
        to: "/Bundles",
        icon: <Book />,
        permission: "Bundles",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "User Orders",
        to: "/UserBundles",
        icon: <Inventory />,
        permission: "UserBundles",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Customer Leaves",
        to: "/CustomerLeaves",
        icon: <ExitToApp />,
        permission: "CustomerLeaves",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Temporary Replacement",
        to: "/TemporaryReplacement",
        icon: <PublishedWithChanges />,
        permission: "TemporaryReplacement",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Extension",
        to: "/Extension",
        icon: <ExtensionTwoTone />,
        permission: "Extension",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Trials",
        to: "/Trials",
        icon: <Bookmarks />,
        permission: "Trials",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
          UserGroupsEnum.PROMOTOR,
        ],
      },
      {
        label: "Trials Converted",
        to: "/TrialsConverted",
        icon: <BookmarkAdded />,
        permission: "TrialsConverted",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Permanent Replacement",
        to: "/PermanentReplacement",
        icon: <FindReplace />,
        permission: "PermanentReplacement",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "StarCleaner Service",
        to: "/StarCleaner",
        icon: <CleaningServicesIcon />,
        permission: "StarCleaner",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "StarCleaner Inventories",
        to: "/StarCleaner-Inventory",
        icon: <Inventory />,
        permission: "StarCleanerInventory",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Renewals",
        to: "/Renewals",
        icon: <AutorenewIcon />,
        permission: "Renewals",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
      {
        label: "Weightage Table",
        to: "/WeightageTable",
        icon: <MonitorWeightIcon />,
        permission: "PriceCalculator",
        requiredGroups: [
          UserGroupsEnum.ADMIN,
          UserGroupsEnum.EXECUTIVE,
          UserGroupsEnum.MANAGER,
        ],
      },
    ],
  },
];

// The cart details view is the BOOK_BY_DISH create flow opened for an existing
// customer's cart — it should light up "Carts", not the ChefForParty children.
const isCartDetailsPage = (pathname: string, search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    pathname === "/ChefForParty/create" &&
    params.get("type") === "BOOK_BY_DISH" &&
    params.has("customerId")
  );
};

const isChildActive = (childTo: string, pathname: string, search: string): boolean => {
  try {
    const toUrl = new URL("http://x" + childTo);
    // Match detail pages too (e.g. /BookByDish/123/show under /BookByDish).
    if (toUrl.pathname !== pathname && !pathname.startsWith(toUrl.pathname + "/")) return false;
    const toFilter = toUrl.searchParams.get("filter");
    if (!toFilter) return true;
    const currentFilter = new URLSearchParams(search).get("filter");
    if (!currentFilter) return false;
    const toObj = JSON.parse(toFilter);
    const currentObj = JSON.parse(decodeURIComponent(currentFilter));
    return toObj.bookingType === currentObj.bookingType;
  } catch {
    return false;
  }
};

const SubMenuItem = ({ item }: { item: MenuItemConfig }) => {
  const [sidebarOpen] = useSidebarState();
  const location = useLocation();

  const anyChildActive = (item.children ?? []).some((child) =>
    isChildActive(child.to, location.pathname, location.search)
  );
  const [open, setOpen] = React.useState(anyChildActive);

  React.useEffect(() => {
    if (anyChildActive) setOpen(true);
  }, [anyChildActive]);

  if (item.children) {

    return (
      <Box>
        <ListItemButton
          onClick={() => setOpen((prev) => !prev)}
          sx={{ pl: sidebarOpen ? 4 : 2, ...(anyChildActive && { color: "primary.main" }) }}
        >
          <ListItemIcon sx={{ minWidth: '20px', width: '36px' }}>{item.icon}</ListItemIcon>
          {sidebarOpen && <ListItemText primary={item.label} />}
          {sidebarOpen && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {item.children.map((child) => {
            const active = isChildActive(child.to, location.pathname, location.search);
            const suppressBuiltInActive =
              !active && isCartDetailsPage(location.pathname, location.search);
            return (
              <MenuItemLink
                key={child.to}
                to={child.to}
                primaryText={child.label}
                leftIcon={child.icon}
                sx={{
                  pl: sidebarOpen ? 6 : 2,
                  // Hide the label text when the sidebar is collapsed —
                  // MenuItemLink always renders it and relies on clipping,
                  // which leaves partial letters visible in the icon rail.
                  ...(!sidebarOpen && { fontSize: 0 }),
                  ...(active && {
                    backgroundColor: "rgba(0,0,0,0.08)",
                    color: "primary.main",
                    fontWeight: 600,
                  }),
                  ...(suppressBuiltInActive && {
                    "&.RaMenuItemLink-active": {
                      color: "inherit",
                      backgroundColor: "transparent",
                      fontWeight: "inherit",
                    },
                  }),
                }}
              />
            );
          })}
        </Collapse>
      </Box>
    );
  }

  const cartDetailsActive =
    item.to === "/PendingCarts" &&
    isCartDetailsPage(location.pathname, location.search);

  return (
    <MenuItemLink
      to={item.to}
      primaryText={item.label}
      leftIcon={item.icon}
      sx={{
        pl: sidebarOpen ? 4 : 2,
        ...(!sidebarOpen && { fontSize: 0 }),
        ...(cartDetailsActive && {
          backgroundColor: "rgba(0,0,0,0.08)",
          color: "primary.main",
          fontWeight: 600,
        }),
      }}
    />
  );
};

const MenuGroup = ({ group, defaultOpen = true }: { group: MenuGroupConfig, defaultOpen?: boolean }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const [sidebarOpen] = useSidebarState();

  const userGroups =
    useAppSelector(
      (state) => state.userDetails.value?.adminUser?.accessGroups
    ) || [];

  const groupVisible = group.items.some((item) =>
    GroupPermissionFunction(userGroups, item.requiredGroups, item.permission)
  );

  if (!groupVisible) return null;

  return (
    <Box>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.1)",
          width: '100%',
        }}
      >
        <ListItemIcon 
          sx={{
            minWidth: '20px',
            width: "36px"
          }}
        >{group.icon}</ListItemIcon>
        {sidebarOpen && <ListItemText primary={group.label} />}
        {sidebarOpen && (open ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {group.items.map((item) => {
          const canSee = GroupPermissionFunction(
            userGroups,
            item.requiredGroups,
            item.permission
          );

          if (!canSee) return null;

          return <SubMenuItem key={item.to} item={item} />;
        })}
      </Collapse>
    </Box>
  );
};

const CustomMenu = () => (
  <>
    {MENU_GROUPS.map((group) => (
      <MenuGroup key={group.id} group={group} defaultOpen={group.id === 'operations'} 

      />
    ))}
  </>
);

export default CustomMenu;
